import { Alert, Box, Paper, Stack, Typography } from "@mui/material";

import { formatPrice } from "../../../utility/formatters";
import { ErrorIcon, SuccessIcon } from "../../Icons/Icons";
import { OperatorBox } from "./ArcCoOperatorBox";
import ArcCoPaymentSummaryBox from "./ArcCoPaymentSummaryBox";

const ArcCoRightPanel = ({ arcCoCalculation, practiceType }) => {
  if (!arcCoCalculation || practiceType === "combined") return null;

  const { actual_revenue, benchmark_guarantee, arcPayment, totalFarmPayment, isPaymentCapped, share_percent } =
    arcCoCalculation;
  const isPaymentTriggered = actual_revenue < benchmark_guarantee;
  const expectedValue = share_percent < 1 ? totalFarmPayment * share_percent : arcPayment || totalFarmPayment || 0;
  const paymentStatus = isPaymentTriggered
    ? { icon: <SuccessIcon color="currentColor" />, text: "Payment Triggered" }
    : { icon: <ErrorIcon color="currentColor" />, text: "No Payment" };

  return (
    <Stack spacing={1.5} sx={{ textAlign: "center" }} className="arc-plc-steps-content">
      <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ borderBottom: 1, borderColor: "colors.borderLightGray", padding: "14px 18px", textAlign: "left" }}
        >
          Payment Trigger
        </Typography>

        <Stack
          sx={{
            padding: { xs: "16px 12px 12px", sm: "20px 18px 16px" },
            justifyContent: "space-between",
            flexDirection: "row",
            alignItems: "stretch",
            gap: { xs: 1, sm: 2 },
            flexWrap: "wrap",
          }}
        >
          <ArcCoPaymentSummaryBox
            title="Actual Revenue"
            value={actual_revenue}
            formatOptions={{ places: 2 }}
            sx={{
              flex: "1 1 0",
              minWidth: 0,
              minHeight: { xs: "80px", sm: "90px" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          />
          <OperatorBox>{isPaymentTriggered ? "<" : "≥"}</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="Guarantee"
            value={benchmark_guarantee}
            formatOptions={{ places: 2 }}
            sx={{
              flex: "1 1 0",
              minWidth: 0,
              minHeight: { xs: "80px", sm: "90px" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          />
        </Stack>

        <Typography variant="body1" color="text.secondary" sx={{ textTransform: "uppercase" }}>
          Payment Status
        </Typography>

        <Box
          sx={{
            backgroundColor: isPaymentTriggered ? "colors.backgroundSuccess" : "colors.backgroundWarning",
            borderRadius: "10rem",
            padding: "14px 18px",
            justifySelf: "center",
            alignItems: "center",
            marginBottom: "20px",
            marginTop: "6px",
            fontSize: "1.125rem",
            fontWeight: 600,
            display: "flex",
            border: 2,
            color: isPaymentTriggered ? "success.main" : "warning.main",
            gap: 1,
          }}
        >
          {paymentStatus.icon}
          {paymentStatus.text}
        </Box>

        {!isPaymentTriggered && (
          <Alert
            severity="info"
            sx={{
              borderRadius: "0.5rem",
              border: 1,
              margin: "0 18px 20px",
              fontWeight: 600,
              color: "info.dark",
            }}
          >
            No ARC-CO payment is triggered because the actual revenue ({formatPrice(actual_revenue, 2)}) is greater than
            or equal to the benchmark guarantee ({formatPrice(benchmark_guarantee, 2)}).
          </Alert>
        )}
      </Paper>

      <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ borderBottom: 1, borderColor: "colors.borderLightGray", padding: "14px 18px", textAlign: "left" }}
        >
          {isPaymentCapped ? "Maximum Payment" : "Expected Payment"}
        </Typography>

        <Box
          color="success.main"
          sx={{
            backgroundColor: "colors.backgroundSuccess",
            borderRadius: "0.5rem",
            padding: "14px 18px",
            justifySelf: "center",
            border: 2,
            margin: 2.5,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {practiceType === "irrigated"
              ? "IRRIGATED PORTION"
              : practiceType === "non-irrigated"
                ? "NON-IRRIGATED PORTION"
                : "EXPECTED PAYMENT"}
          </Typography>

          <Typography
            sx={{ fontWeight: "700 !important", fontSize: "1.5rem", paddingTop: "0.5rem", wordBreak: "break-all" }}
          >
            {formatPrice(expectedValue, 0)}
          </Typography>
        </Box>
      </Paper>
    </Stack>
  );
};

export default ArcCoRightPanel;
